import * as React from "react";
import { Button, Space } from "antd";
import { ExternalLink } from "../../../../newcomponents/table/components/ExternalLink";
import { CloudAccountConfigAzureFunctionLocationOptions } from "../../utils";
import { formSetValue, useCpFormValue } from "../../../gvc/testNewForm/utils";
import { CpLabel } from "../../../gvc/testNewForm/CpLabel";
import { CpFormItem } from "../../../gvc/testNewForm/CpFormItem";
import { CpErrorWarning } from "../../../gvc/testNewForm/CpErrorWarning";
import { CpInput } from "../../../gvc/testNewForm/CpInput";
import { CpSelect } from "../../../gvc/testNewForm/CpSelect";
import { useCpFormContext } from "../../../../components/form/useCpFormContext";
import { SyntaxHighlight } from "../../../../components/syntaxHighlight/SyntaxHighlight";

interface Props {}
export const CloudaccountCreateAzureConfigurationFunction: React.FC<Props> = () => {
  const { id } = useCpFormContext();

  const subscriptionId = useCpFormValue("azure.subscriptionId");
  const resourceGroup = useCpFormValue("azure.resourceGroupName");
  const functionApp = useCpFormValue("azure.functionAppName");
  const storageAccount = useCpFormValue("azure.storageAccountName");
  const location = useCpFormValue("azure.locationName");
  const cliType = useCpFormValue("azure.cliType");

  const allFieldsFilled = subscriptionId && resourceGroup && storageAccount && location && functionApp;

  const step1Linux = `az functionapp create \\
  --subscription ${subscriptionId} \\
  --resource-group ${resourceGroup} \\
  --name ${functionApp} \\
  --storage-account ${storageAccount} \\
  --assign-identity \\
  --role Owner \\
  --scope /subscriptions/${subscriptionId} \\
  --os-type linux \\
  --runtime node \\
  --functions-version 4 \\
  --runtime-version 24 \\
  --disable-app-insights \\
  --consumption-plan-location ${location}`;

  const step1Windows = `az functionapp create --subscription ${subscriptionId} --resource-group ${resourceGroup} --name ${functionApp} --storage-account ${storageAccount} --assign-identity --role Owner --scope /subscriptions/${subscriptionId} --os-type linux --runtime node --functions-version 4 --runtime-version 24 --disable-app-insights --consumption-plan-location ${location}`;

  const step2 = `wget https://storage.googleapis.com/artifacts.cpln-build.appspot.com/binaries/azure-cpln-connector/latest/azure-cpln-connector.zip`;

  const step3Linux = `az functionapp deployment source config-zip \\
  --subscription ${subscriptionId} \\
  --resource-group ${resourceGroup} \\
  --name ${functionApp} \\
  --src azure-cpln-connector.zip`;

  const step3Windows = `az functionapp deployment source config-zip --subscription ${subscriptionId} --resource-group ${resourceGroup} --name ${functionApp} --src azure-cpln-connector.zip`;

  const step4Linux = `az functionapp function show \\
  --subscription ${subscriptionId} \\
  --resource-group ${resourceGroup} \\
  --name ${functionApp} \\
  --function iam-broker \\
  --query invokeUrlTemplate \\
  -o tsv`;

  const step4Windows = `az functionapp function show --subscription ${subscriptionId} --resource-group ${resourceGroup} --name ${functionApp} --function iam-broker --query invokeUrlTemplate -o tsv`;

  const step5Linux = `az functionapp function keys list \\
  --subscription ${subscriptionId} \\
  --resource-group ${resourceGroup} \\
  --name ${functionApp} \\
  --function-name iam-broker \\
  --query default \\
  -o tsv`;

  const step5Windows = `az functionapp function keys list --subscription ${subscriptionId} --resource-group ${resourceGroup} --name ${functionApp} --function-name iam-broker --query default -o tsv`;

  return (
    <ul style={{ paddingLeft: 24 }} key={cliType}>
      <AzureCLISteps />
      <li className="list-decimal my-2">
        Enter the name of an existing Azure resource group and storage account then select a deployment location. These
        values will be used to generate the commands in the next steps.
      </li>
      <CpFormItem>
        <CpLabel name={"azure.resourceGroupName"}>Resource Group Name</CpLabel>
        <CpInput name={"azure.resourceGroupName"} />
        <CpErrorWarning name={"azure.resourceGroupName"} />
      </CpFormItem>
      <CpFormItem>
        <CpLabel name={"azure.storageAccountName"}>Storage Account Name</CpLabel>
        <CpInput name={"azure.storageAccountName"} />
        <CpErrorWarning name={"azure.storageAccountName"} />
      </CpFormItem>
      <CpFormItem>
        <CpLabel name={"azure.locationName"}>Location Name</CpLabel>
        <CpSelect name={"azure.locationName"} options={CloudAccountConfigAzureFunctionLocationOptions} />
        <CpErrorWarning name={"azure.locationName"} />
      </CpFormItem>
      <CpFormItem>
        <CpLabel name={"azure.functionAppName"}>Function App Name</CpLabel>
        <CpInput name={"azure.functionAppName"} />
        <CpErrorWarning name={"azure.functionAppName"} />
      </CpFormItem>
      {allFieldsFilled ? (
        <>
          <li className="list-decimal my-2">Execute the commands below:</li>
          <Space.Compact>
            <Button
              htmlType={`button`}
              style={{ borderTopRightRadius: 0, borderBottomRightRadius: 0 }}
              size={"small"}
              type={cliType === "linux/mac" ? "primary" : "default"}
              onClick={() => formSetValue(id, "azure.cliType", "linux/mac")}
            >
              Linux/MacOS
            </Button>
            <Button
              htmlType={`button`}
              style={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}
              size={"small"}
              type={cliType === "windows" ? "primary" : "default"}
              onClick={() => formSetValue(id, "azure.cliType", "windows")}
            >
              Windows
            </Button>
          </Space.Compact>
          <div className="my-2" style={{ maxWidth: 700 }}>
            <SyntaxHighlight code={cliType === "linux/mac" ? step1Linux : step1Windows} language="bash" />
          </div>
          <div className="my-2" style={{ maxWidth: 700 }}>
            <SyntaxHighlight code={step2} language="bash" />
          </div>
          <div className="my-2" style={{ maxWidth: 700 }}>
            <SyntaxHighlight code={cliType === "linux/mac" ? step3Linux : step3Windows} language="bash" />
          </div>
        </>
      ) : null}
      {allFieldsFilled ? (
        <>
          <li className="list-decimal my-2">
            Execute the command below to get the URL of the deployed function and enter it into the text box below:
          </li>
          <Space.Compact>
            <Button
              htmlType="button"
              style={{ borderTopRightRadius: 0, borderBottomRightRadius: 0 }}
              size={"small"}
              type={cliType === "linux/mac" ? "primary" : "default"}
              onClick={() => formSetValue(id, "azure.cliType", "linux/mac")}
            >
              Linux/MacOS
            </Button>
            <Button
              htmlType={`button`}
              style={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}
              size={"small"}
              type={cliType === "windows" ? "primary" : "default"}
              onClick={() => formSetValue(id, "azure.cliType", "windows")}
            >
              Windows
            </Button>
          </Space.Compact>
          <div className="my-2" style={{ maxWidth: 700 }}>
            <SyntaxHighlight code={cliType === "linux/mac" ? step4Linux : step4Windows} language="bash" />
          </div>
          <CpFormItem>
            <CpLabel name={"azure.url"}>URL</CpLabel>
            <CpInput name={"azure.url"} />
            <CpErrorWarning name={"azure.url"} />
          </CpFormItem>
        </>
      ) : null}
      {allFieldsFilled ? (
        <>
          <li className="list-decimal my-2">
            Execute the command below to get the code of the deployed function and enter it into the text box below:
          </li>
          <Space.Compact>
            <Button
              htmlType={`button`}
              style={{ borderTopRightRadius: 0, borderBottomRightRadius: 0 }}
              size={"small"}
              type={cliType === "linux/mac" ? "primary" : "default"}
              onClick={() => formSetValue(id, "azure.cliType", "linux/mac")}
            >
              Linux/MacOS
            </Button>
            <Button
              htmlType={`button`}
              style={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}
              size={"small"}
              type={cliType === "windows" ? "primary" : "default"}
              onClick={() => formSetValue(id, "azure.cliType", "windows")}
            >
              Windows
            </Button>
          </Space.Compact>
          <div className="my-2" style={{ maxWidth: 700 }}>
            <SyntaxHighlight code={cliType === "linux/mac" ? step5Linux : step5Windows} language="bash" />
          </div>
          <CpFormItem>
            <CpLabel name={"azure.code"}>Code</CpLabel>
            <CpInput name={"azure.code"} />
            <CpErrorWarning name={"azure.code"} />
          </CpFormItem>
        </>
      ) : null}
    </ul>
  );
};

const AzureCLISteps: React.FC = () => {
  return (
    <>
      <li className="list-decimal mb-6">
        <ExternalLink
          url="https://docs.microsoft.com/en-us/cli/azure/install-azure-cli"
          label="Install the Azure CLI"
          disableCopy
        />{" "}
        and sign in with your Azure account using the{" "}
        <ExternalLink
          url="https://docs.microsoft.com/en-us/cli/azure/authenticate-azure-cli"
          label="Azure CLI"
          disableCopy
        />
        .
        <div className="mt-2">
          Alternatively, you can use{" "}
          <ExternalLink url="https://shell.azure.com/bash" label="Azure Cloud Shell" disableCopy /> which has the Azure
          CLI pre-installed and pre-authenticated.
        </div>
      </li>
    </>
  );
};
